import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Store, User, FileText, FileUp, Key, Calendar, RefreshCw, AlertTriangle,
  Pencil, Check, X, Eye, Download, Upload, MapPin, Phone, Mail, IdCard,
  Image, Building, Hash, Loader2, ExternalLink, ZoomIn
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get nested value with fallbacks
const getValue = (obj, ...paths) => {
  for (const path of paths) {
    const parts = path.split('.');
    let val = obj;
    for (const part of parts) {
      val = val?.[part];
      if (val === undefined || val === null) break;
    }
    if (val !== undefined && val !== null && val !== '') return val;
  }
  return '';
};

// Helper to format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

// Helper to format expiry date info
const formatExpiryDate = (dateStr) => {
  if (!dateStr) return { text: 'No subscription', isExpired: true, isExpiringSoon: false, daysRemaining: null };
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    return {
      text: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
      isExpired: diffDays < 0,
      isExpiringSoon: diffDays >= 0 && diffDays <= 30,
      daysRemaining: diffDays >= 0 ? diffDays : null
    };
  } catch {
    return { text: dateStr, isExpired: false, isExpiringSoon: false, daysRemaining: null };
  }
};

// Inline Editable Field Component
const EditableField = ({ label, value, fieldKey, onSave, disabled = false, type = 'text', options = [], placeholder = '' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(fieldKey, editValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value || '');
    setIsEditing(false);
  };

  if (disabled) {
    return (
      <div>
        <p className="text-sm text-secondary font-medium flex items-center gap-1">
          {label}
          <span className="text-xs text-muted-foreground">(Cannot edit)</span>
        </p>
        <p className="text-base text-primary">{value || 'Not provided'}</p>
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className="space-y-1">
        <Label className="text-sm text-secondary font-medium">{label}</Label>
        <div className="flex items-center gap-2">
          {type === 'select' ? (
            <Select value={editValue} onValueChange={setEditValue}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder={placeholder || `Select ${label}`} />
              </SelectTrigger>
              <SelectContent>
                {options.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : type === 'textarea' ? (
            <Textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder={placeholder}
              className="min-h-[80px]"
            />
          ) : (
            <Input
              type={type}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder={placeholder}
              className="h-9"
            />
          )}
          <Button size="sm" variant="ghost" onClick={handleSave} disabled={saving} className="h-9 px-2 text-green-600 hover:text-green-700 hover:bg-green-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={handleCancel} disabled={saving} className="h-9 px-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group cursor-pointer" onClick={() => setIsEditing(true)}>
      <p className="text-sm text-secondary font-medium flex items-center gap-1">
        {label}
        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground" />
      </p>
      <p className="text-base text-primary group-hover:text-primary/80 transition-colors">
        {value || <span className="text-muted-foreground italic">Click to add</span>}
      </p>
    </div>
  );
};

// Document/Image Viewer Component
const DocumentViewer = ({ doc, label, icon: Icon = FileText, onUpload, uploading }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  
  const isImage = doc?.type?.startsWith('image/') || 
    doc?.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ||
    doc?.data?.startsWith('data:image');
  
  const hasDoc = doc && (doc.data || doc.url);
  const docUrl = doc?.data || doc?.url;

  const handleDownload = () => {
    if (!docUrl) return;
    const link = document.createElement('a');
    link.href = docUrl;
    link.download = doc?.name || `${label.replace(/\s+/g, '_')}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div className="border rounded-lg p-3 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">{label}</span>
          </div>
        </div>
        
        {hasDoc ? (
          <div className="mt-2">
            {isImage ? (
              <div 
                className="relative cursor-pointer group"
                onClick={() => setPreviewOpen(true)}
              >
                <img 
                  src={docUrl} 
                  alt={label}
                  className="w-full h-24 object-cover rounded border group-hover:opacity-80 transition-opacity"
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-black/50 rounded-full p-2">
                    <ZoomIn className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-2 bg-muted/50 rounded text-sm">
                <FileText className="w-4 h-4 text-muted-foreground" />
                <span className="truncate flex-1">{doc?.name || 'Document'}</span>
              </div>
            )}
            <div className="flex items-center gap-2 mt-2">
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1 h-8 text-xs"
                onClick={() => isImage ? setPreviewOpen(true) : window.open(docUrl, '_blank')}
              >
                <Eye className="w-3 h-3 mr-1" /> View
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="flex-1 h-8 text-xs"
                onClick={handleDownload}
              >
                <Download className="w-3 h-3 mr-1" /> Download
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-2">
            <label className="cursor-pointer flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload {label}
                </>
              )}
              <input 
                type="file" 
                accept={isImage ? 'image/*' : '.pdf,.doc,.docx,image/*'}
                className="hidden"
                onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])}
                disabled={uploading}
              />
            </label>
          </div>
        )}
      </div>

      {/* Full Size Image Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center">
            <img 
              src={docUrl} 
              alt={label}
              className="max-w-full max-h-[70vh] object-contain rounded"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Main Component
const FranchiseDetailsView = ({ 
  franchise, 
  subscriptionInfo,
  onUpdate, 
  onChangePinClick,
  onRenewClick 
}) => {
  const [saving, setSaving] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);

  if (!franchise) return null;

  const franchiseId = franchise.franchise_id || franchise.id;
  const expiryInfo = formatExpiryDate(subscriptionInfo?.expiry_date || franchise.subscription_expiry_date);
  const isInherited = subscriptionInfo?.inherited_from_client;

  // Get values from the data structure
  const outletName = getValue(franchise, 'outlet_name', 'name', 'franchise_name');
  const outletAddress = franchise.outlet_address || franchise.address || {};
  const ownerDetails = franchise.owner_details || {};
  const documents = franchise.documents || {};

  const handleFieldSave = async (fieldKey, value) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build update payload based on field path
      let updateData = {};
      if (fieldKey.startsWith('outlet_address.')) {
        const subKey = fieldKey.replace('outlet_address.', '');
        updateData = { outlet_address: { ...outletAddress, [subKey]: value } };
      } else if (fieldKey.startsWith('owner_details.')) {
        const subKey = fieldKey.replace('owner_details.', '');
        updateData = { owner_details: { ...ownerDetails, [subKey]: value } };
      } else {
        updateData = { [fieldKey]: value };
      }

      await axios.put(
        `${API_URL}/api/franchises/${franchiseId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Updated successfully');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error(error.response?.data?.detail || 'Failed to update');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = async (docType, file) => {
    setUploadingDoc(docType);
    try {
      const token = localStorage.getItem('token');
      
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result;
        const docData = {
          name: file.name,
          type: file.type,
          size: file.size,
          data: base64Data
        };

        // Determine where to save based on document type
        let updateData = {};
        if (['id_proof_doc', 'photo'].includes(docType)) {
          updateData = { 
            owner_details: { 
              ...ownerDetails, 
              [docType]: docData 
            } 
          };
        } else {
          updateData = { 
            documents: { 
              ...documents, 
              [docType]: docData 
            } 
          };
        }

        await axios.put(
          `${API_URL}/api/franchises/${franchiseId}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success('Document uploaded successfully');
        if (onUpdate) onUpdate();
        setUploadingDoc(null);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error('Failed to upload document');
      setUploadingDoc(null);
    }
  };

  const idProofTypes = [
    { value: 'aadhaar', label: 'Aadhaar Card' },
    { value: 'pan', label: 'PAN Card' },
    { value: 'passport', label: 'Passport' },
    { value: 'driving_license', label: 'Driving License' },
    { value: 'voter_id', label: 'Voter ID' },
  ];

  return (
    <div className="space-y-6" data-testid="franchise-details-view">
      {/* Section 0: Franchise Info (ID, PIN, Status, Subscription) */}
      <div className="space-y-4">
        {/* Franchise ID & PIN Box */}
        <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-sm text-secondary font-medium mb-1">Franchise ID</p>
              <p className="text-xl font-bold text-primary font-mono tracking-wide">
                {franchiseId || 'Not assigned'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-secondary font-medium mb-1">Login PIN</p>
              <div className="flex items-center gap-2">
                <p className="text-xl font-bold font-mono">{franchise.pin || franchise.passcode || 'N/A'}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onChangePinClick}
                  className="text-xs border-amber-500 text-amber-600 hover:bg-amber-50"
                >
                  <Key className="h-3 w-3 mr-1" />
                  Change
                </Button>
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-primary/10 flex items-center gap-4">
            <div>
              <p className="text-xs text-secondary">Status</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                franchise.status === 'active' || franchise.is_active 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {franchise.status === 'active' || franchise.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <p className="text-xs text-secondary">Created</p>
              <p className="text-sm font-medium">{formatDate(franchise.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Subscription Status Box */}
        <div className={`rounded-lg p-4 border ${
          expiryInfo.isExpired 
            ? 'bg-red-50 border-red-200' 
            : expiryInfo.isExpiringSoon 
              ? 'bg-yellow-50 border-yellow-200'
              : 'bg-green-50 border-green-200'
        }`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${
                expiryInfo.isExpired ? 'bg-red-100' : expiryInfo.isExpiringSoon ? 'bg-yellow-100' : 'bg-green-100'
              }`}>
                {expiryInfo.isExpired ? (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                ) : (
                  <Calendar className="h-5 w-5 text-green-600" />
                )}
              </div>
              <div>
                <p className={`text-sm font-medium ${
                  expiryInfo.isExpired ? 'text-red-700' : expiryInfo.isExpiringSoon ? 'text-yellow-700' : 'text-green-700'
                }`}>
                  Subscription {expiryInfo.isExpired ? 'Expired' : 'Active'}
                  {isInherited && !expiryInfo.isExpired && (
                    <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      From Business Plan
                    </span>
                  )}
                </p>
                <p className={`text-lg font-bold ${expiryInfo.isExpired ? 'text-red-800' : 'text-gray-800'}`}>
                  {expiryInfo.isExpired 
                    ? (expiryInfo.text === 'No subscription' ? 'No Active Subscription' : `Expired on ${expiryInfo.text}`)
                    : `Valid until ${expiryInfo.text}`
                  }
                </p>
                {!expiryInfo.isExpired && expiryInfo.daysRemaining !== null && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {expiryInfo.daysRemaining} day{expiryInfo.daysRemaining !== 1 ? 's' : ''} remaining
                  </p>
                )}
              </div>
            </div>
            <Button
              onClick={onRenewClick}
              className={`${
                expiryInfo.isExpired 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-amber-600 hover:bg-amber-700'
              } text-white`}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {expiryInfo.isExpired ? 'Reactivate' : 'Renew'}
            </Button>
          </div>
        </div>
      </div>

      {/* Section 1: Franchise Details */}
      <div className="bg-muted/30 rounded-xl p-4 space-y-4">
        <h4 className="font-semibold text-lg text-primary flex items-center gap-2">
          <Store className="w-5 h-5" />
          Franchise Details
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <EditableField
              label="Outlet Name"
              value={outletName}
              fieldKey="outlet_name"
              onSave={handleFieldSave}
              placeholder="Enter outlet name"
            />
          </div>
          
          <EditableField
            label="Pincode"
            value={getValue(franchise, 'outlet_address.pincode', 'pincode')}
            fieldKey="outlet_address.pincode"
            onSave={handleFieldSave}
            placeholder="Enter pincode"
          />
          
          <EditableField
            label="State"
            value={getValue(franchise, 'outlet_address.state', 'state')}
            fieldKey="outlet_address.state"
            onSave={handleFieldSave}
            placeholder="Enter state"
          />
          
          <EditableField
            label="District"
            value={getValue(franchise, 'outlet_address.district', 'district')}
            fieldKey="outlet_address.district"
            onSave={handleFieldSave}
            placeholder="Enter district"
          />
          
          <EditableField
            label="City/Town"
            value={getValue(franchise, 'outlet_address.city', 'city')}
            fieldKey="outlet_address.city"
            onSave={handleFieldSave}
            placeholder="Enter city"
          />
          
          <div className="md:col-span-2">
            <EditableField
              label="Full Address"
              value={getValue(franchise, 'outlet_address.full_address', 'address.line1', 'franchise_address')}
              fieldKey="outlet_address.full_address"
              onSave={handleFieldSave}
              type="textarea"
              placeholder="Enter full address"
            />
          </div>

          {/* Map Location */}
          {(franchise.outlet_location?.lat || franchise.location?.lat) && (
            <div className="md:col-span-2">
              <p className="text-sm text-secondary font-medium mb-1">Location on Map</p>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">
                  {(franchise.outlet_location?.lat || franchise.location?.lat)?.toFixed(6)}, 
                  {(franchise.outlet_location?.lng || franchise.location?.lng)?.toFixed(6)}
                </span>
                <a 
                  href={`https://www.google.com/maps?q=${franchise.outlet_location?.lat || franchise.location?.lat},${franchise.outlet_location?.lng || franchise.location?.lng}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> View on Maps
                </a>
              </div>
            </div>
          )}

          {/* Rent Agreement Document */}
          <div className="md:col-span-2">
            <p className="text-sm text-secondary font-medium mb-2">Rent Agreement / Address Proof</p>
            <DocumentViewer
              doc={documents.rent_agreement}
              label="Rent Agreement"
              icon={FileText}
              onUpload={(file) => handleDocumentUpload('rent_agreement', file)}
              uploading={uploadingDoc === 'rent_agreement'}
            />
          </div>

          {/* GST Applicable */}
          <EditableField
            label="GST Applicable"
            value={franchise.is_gst_applicable || franchise.gst_applicable ? 'Yes' : 'No'}
            fieldKey="is_gst_applicable"
            onSave={(key, val) => handleFieldSave(key, val === 'Yes')}
            type="select"
            options={[{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }]}
          />

          {(franchise.is_gst_applicable || franchise.gst_applicable) && (
            <EditableField
              label="GSTIN"
              value={getValue(franchise, 'outlet_gstin', 'gstin')}
              fieldKey="outlet_gstin"
              onSave={handleFieldSave}
              placeholder="Enter GSTIN"
            />
          )}
        </div>
      </div>

      {/* Section 2: Owner / Incharge Details */}
      <div className="bg-muted/30 rounded-xl p-4 space-y-4">
        <h4 className="font-semibold text-lg text-primary flex items-center gap-2">
          <User className="w-5 h-5" />
          Owner / Incharge Details
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <EditableField
            label="Owner Full Name"
            value={getValue(franchise, 'owner_details.full_name', 'owner_name', 'contact_person')}
            fieldKey="owner_details.full_name"
            onSave={handleFieldSave}
            placeholder="Enter owner name"
          />
          
          {/* Non-editable fields */}
          <EditableField
            label="Phone Number"
            value={getValue(franchise, 'owner_details.phone', 'mobile_number', 'contact_number')}
            fieldKey="owner_details.phone"
            disabled={true}
          />
          
          <EditableField
            label="Email Address"
            value={getValue(franchise, 'owner_details.email', 'email')}
            fieldKey="owner_details.email"
            disabled={true}
          />
          
          <div className="md:col-span-2">
            <EditableField
              label="Owner Full Address"
              value={getValue(franchise, 'owner_details.full_address', 'owner_address')}
              fieldKey="owner_details.full_address"
              onSave={handleFieldSave}
              type="textarea"
              placeholder="Enter owner's full address"
            />
          </div>

          <EditableField
            label="Identity Proof Type"
            value={getValue(franchise, 'owner_details.id_proof_type', 'identity_proof_type')}
            fieldKey="owner_details.id_proof_type"
            onSave={handleFieldSave}
            type="select"
            options={idProofTypes}
          />

          {/* ID Proof Document */}
          <div>
            <p className="text-sm text-secondary font-medium mb-2">Identity Proof Document</p>
            <DocumentViewer
              doc={ownerDetails.id_proof_doc}
              label="ID Proof"
              icon={IdCard}
              onUpload={(file) => handleDocumentUpload('id_proof_doc', file)}
              uploading={uploadingDoc === 'id_proof_doc'}
            />
          </div>

          {/* Passport Photo */}
          <div>
            <p className="text-sm text-secondary font-medium mb-2">Passport Size Photo</p>
            <DocumentViewer
              doc={ownerDetails.photo}
              label="Photo"
              icon={Image}
              onUpload={(file) => handleDocumentUpload('photo', file)}
              uploading={uploadingDoc === 'photo'}
            />
          </div>
        </div>
      </div>

      {/* Section 3: Upload Documents */}
      <div className="bg-muted/30 rounded-xl p-4 space-y-4">
        <h4 className="font-semibold text-lg text-primary flex items-center gap-2">
          <FileUp className="w-5 h-5" />
          Documents
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <DocumentViewer
            doc={documents.franchise_agreement}
            label="Franchise Agreement"
            icon={FileText}
            onUpload={(file) => handleDocumentUpload('franchise_agreement', file)}
            uploading={uploadingDoc === 'franchise_agreement'}
          />
          
          <DocumentViewer
            doc={documents.fssai_license}
            label="FSSAI License"
            icon={FileText}
            onUpload={(file) => handleDocumentUpload('fssai_license', file)}
            uploading={uploadingDoc === 'fssai_license'}
          />
          
          <DocumentViewer
            doc={documents.trade_license}
            label="Trade License"
            icon={FileText}
            onUpload={(file) => handleDocumentUpload('trade_license', file)}
            uploading={uploadingDoc === 'trade_license'}
          />

          {(franchise.is_gst_applicable || franchise.gst_applicable) && (
            <DocumentViewer
              doc={documents.gst_certificate}
              label="GST Certificate"
              icon={FileText}
              onUpload={(file) => handleDocumentUpload('gst_certificate', file)}
              uploading={uploadingDoc === 'gst_certificate'}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FranchiseDetailsView;
